import { useQuery } from '@tanstack/react-query';
import _ from 'lodash';
import React, { forwardRef, useImperativeHandle } from 'react';
import { DataItem } from '../../../@types/DataItem';
import { checkIfIsLoading } from '../../../utils';
import { InnerFilter } from '../../FilterSection';
import { SegmentsPlaceholder } from '../../NewFilterBox/Components';
import { CheckPillProps } from '../CheckPill';

export type AsyncCheckPillProps = {
  data?: DataItem[];
  value?: string[];
  placeholder?: string;
  dataKey: string;
  queryKey?: string[];
  queryFn?: (query: string) => Promise<DataItem[]>;
  onSearch?: (query: string) => void;
  onSelect?: (value: string[], item: DataItem) => void;
  onClean?: () => void;
  level?: number;
  selectAll?: boolean;
  disabled?: boolean;
  /** Mostra o botão "Colar lista de códigos", igual ao filtro de produtos do Gerenciador */
  pasteValues?: boolean;
  /** Busca usada para o texto colado (ex.: listProducts com isModal) */
  pasteQueryFn?: (query: string) => Promise<DataItem[]>;
};

export const AsyncCheckPill = forwardRef(
  (
    {
      value = [],
      dataKey = '',
      queryKey = [],
      level,
      placeholder,
      selectAll,
      queryFn,
      onSearch,
      onSelect,
      onClean,
      disabled,
      pasteValues = false,
      pasteQueryFn,
      ...props
    }: AsyncCheckPillProps,
    ref,
  ) => {
    const [search, setSearch] = React.useState('');

    const [data, setData] = React.useState<DataItem[]>([]);

    const [cache, setCache] = React.useState<DataItem[]>([]);

    // Mesmo fluxo do filtro de produtos do Gerenciador (ProductsPill): o texto colado vira a busca,
    // vai para o backend pelo pasteQueryFn e tudo o que volta é somado à seleção atual. Guarda o próprio
    // texto colado (e não só uma flag) para que só a resposta dessa busca seja tratada como colagem.
    const [pastedQuery, setPastedQuery] = React.useState<string | null>(null);
    const isPastedQuery = pastedQuery !== null && search === pastedQuery;

    const timer = React.useRef<NodeJS.Timeout>();

    const handleSearch = (query: string) => {
      clearTimeout(timer.current);
      const newTimer = setTimeout(() => {
        setSearch(query);
        onSearch?.(query);
      }, 500);
      timer.current = newTimer;
    };

    const handleClose = () => {
      setSearch('');
      onSearch?.('');
    };

    const handleSuccess = (data: DataItem[]) => {
      const uniqueData = _.uniqBy([...cache, ...data], 'value');
      setData(uniqueData);
      if (!isPastedQuery) return;
      onSelect?.(
        uniqueData.map(item => item.value),
        {} as DataItem,
      );
      setCache(uniqueData);
      setPastedQuery(null);
    };

    const handleConfirmPasteValues = (query: string) => {
      if (!query.length) return;
      setPastedQuery(query);
      handleSearch(query);
    };

    const fetchData = async () => {
      if (!isPastedQuery || !pasteQueryFn) return queryFn?.(search);
      try {
        return await pasteQueryFn(search);
      } catch (error) {
        setPastedQuery(null);
        throw error;
      }
    };

    const handleSelect = (value: string[], item: DataItem) => {
      onSelect?.(value, item);

      const updatedList = data.filter(dataItem => {
        return value.includes(dataItem.value);
      });

      setCache(updatedList);
    };

    const handleClean = () => {
      setCache([]);
      handleSelect([], {} as DataItem);
      onClean?.();
      setSearch('');
    };

    const handleSelectAll = () => {
      if (data.length === value.length) handleClean();
      else {
        handleSelect(
          data.map(item => item.value),
          {} as DataItem,
        );
      }
    };

    const { fetchStatus } = useQuery({
      queryKey: [search, dataKey, ...queryKey],
      initialData: [],
      retry: false,
      keepPreviousData: true,
      enabled: !!queryFn,
      queryFn: fetchData,
      onSuccess: handleSuccess,
    });

    useImperativeHandle(ref, () => ({
      clean: () => {
        handleClean();
      },
    }));

    return (
      <InnerFilter
        disabled={disabled}
        data={data as CheckPillProps['data']}
        value={value}
        isLoading={checkIfIsLoading(fetchStatus)}
        onSearch={handleSearch}
        onSelect={(value, item) => {
          handleSelect?.(value, item as DataItem);
        }}
        onClose={handleClose}
        onClean={handleClean}
        pasteValues={pasteValues}
        search={search}
        onConfirmPasteValues={handleConfirmPasteValues}
        placeholder={(level ? <SegmentsPlaceholder level={level as number} placeholder={placeholder as string} /> : placeholder) as string}
        selectAll={
          selectAll
            ? {
                onSelectAll: handleSelectAll,
              }
            : undefined
        }
        {...props}
      />
    );
  },
);

AsyncCheckPill.displayName = 'AsyncCheckPill';
